import React, { useState, useEffect } from 'react';
import { Box, AppBar, Toolbar, Typography, Button } from '@mui/material';
import SemanticSearchAdmin from './SemanticSearchAdmin';
import SemanticSearch from './SemanticSearch';
import TokenManager from './TokenManager';
import { TokenListPage } from './TokenListPage';
import AuditLogsManager from './AuditLogsManager';
import BillingPage from './BillingPage';
import UserConsumptionPage from './UserConsumptionPage';
import PricingManagementPage from './PricingManagementPage';
import Login from './Login';

function App() {
  const [currentPage, setCurrentPage] = useState<'search' | 'admin' | 'token' | 'manage-tokens' | 'audit' | 'billing' | 'consumption' | 'pricing'>('search');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);

  // Check if user is already authenticated on mount
  useEffect(() => {
    const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
    if (token) {
      setAuthToken(token);
      setIsAuthenticated(true);
    }
  }, []);

  const handleLoginSuccess = (token: string) => {
    setAuthToken(token);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setAuthToken(null);
    setIsAuthenticated(false);
    setCurrentPage('search');
  };

  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            🚀 RAAS Semantic Search Microservice
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              color={currentPage === 'search' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('search')}
              variant={currentPage === 'search' ? 'outlined' : 'text'}
              size="small"
            >
              🔍 Search
            </Button>
            <Button
              color={currentPage === 'admin' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('admin')}
              variant={currentPage === 'admin' ? 'outlined' : 'text'}
              size="small"
            >
              ⚙️ Semantic-adm
            </Button>
            <Button
              color={currentPage === 'token' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('token')}
              variant={currentPage === 'token' ? 'outlined' : 'text'}
              size="small"
            >
              🔐 Token-adm
            </Button>
            <Button
              color={currentPage === 'manage-tokens' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('manage-tokens')}
              variant={currentPage === 'manage-tokens' ? 'outlined' : 'text'}
              size="small"
            >
              📋 My Tokens
            </Button>
            <Button
              color={currentPage === 'audit' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('audit')}
              variant={currentPage === 'audit' ? 'outlined' : 'text'}
              size="small"
            >
              📊 Audit Logs
            </Button>
            <Button
              color={currentPage === 'billing' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('billing')}
              variant={currentPage === 'billing' ? 'outlined' : 'text'}
              size="small"
            >
              💰 Billing
            </Button>
            <Button
              color={currentPage === 'consumption' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('consumption')}
              variant={currentPage === 'consumption' ? 'outlined' : 'text'}
              size="small"
            >
              📊 Consumption
            </Button>
            <Button
              color={currentPage === 'pricing' ? 'secondary' : 'inherit'}
              onClick={() => setCurrentPage('pricing')}
              variant={currentPage === 'pricing' ? 'outlined' : 'text'}
              size="small"
            >
              💲 Pricing
            </Button>
            <Button
              color="inherit"
              onClick={handleLogout}
              variant="text"
              size="small"
              sx={{ ml: 2 }}
            >
              🚪 Logout
            </Button>
          </Box>
          <Typography variant="caption" sx={{ ml: 2 }}>Port 3110</Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', width: '100%', overflowX: 'auto' }}>
        {currentPage === 'search' && <SemanticSearch />}
        {currentPage === 'admin' && <SemanticSearchAdmin />}
        {currentPage === 'token' && <TokenManager />}
        {currentPage === 'manage-tokens' && <TokenListPage authToken={authToken || ''} />}
        {currentPage === 'audit' && <AuditLogsManager />}
        {currentPage === 'billing' && <BillingPage />}
        {currentPage === 'consumption' && <UserConsumptionPage />}
        {currentPage === 'pricing' && <PricingManagementPage />}
      </Box>
    </Box>
  );
}

export default App;